import React, { useEffect, useState } from 'react'
import {
  SafeAreaView,
  View,
  Text,
  TouchableOpacity,
  FlatList,
  Image,
} from 'react-native'
import LinearGradient from 'react-native-linear-gradient'
import {
  COMMON_STYLES,
  formatDate,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import { ROUTES as FARMER_360_ROUTES } from 'app/farmer360/routes/types'

import { PreviousDiagnosisScreenProps } from '../../routes/types'
import DiagnosisCard from '../components/DiagnosisCard'
import FilterButton from '../components/filter/FilterButton'
import FilterModal, { FORM_INPUT_TYPE } from '../components/filter/FilterModal'
import { BACK_ARROW_BUTTON } from '../assests/images'
import useUserDiagnosisHistory from '../hooks/useUserDiagnosisHistory'
import { rnNavigation } from 'app/utils/appConfig'
import { setPlantixUId, selectFarmerId, selectFarmerName } from 'app/farmer360/store/app'
import { useAppDispatch, useAppSelector } from 'app/hooks/reduxHooks'
import { trackEvent } from 'app/farmer360/utils/tracking'
import { CustomEventTypes } from '../utils/events/CustomEventTypes'
import { useRedirectOnRefresh } from '../hooks/useRedirectOnRefresh'

interface DateFilterRange {
  label: string
  startDate: string
  endDate: string
}

const PreviousDiagnosis = ({ navigation }: PreviousDiagnosisScreenProps) => {
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [dateFilter, setDateFilter] = useState<DateFilterRange | null>(null)
  const farmerId = useAppSelector(selectFarmerId)
  const farmerName= useAppSelector(selectFarmerName)
  const dispatch = useAppDispatch()

  const {
    diagnosisData,
    loading,
    hasMore,
    page,
    fetchNextPage,
    resetData,
    isFilteredEmpty,
  } = useUserDiagnosisHistory(farmerId, dateFilter)

  useRedirectOnRefresh({
    currentRoute: FARMER_360_ROUTES.PREVIOUS_DIAGNOSIS,
    redirectRoute: FARMER_360_ROUTES.DISEASE_DIAGNOSIS,
    rnNavigation,
    skipRedirect: false,
  });

  const computeRange = (label: string): DateFilterRange => {
    const now = new Date()
    const end = new Date(now)
    const start = new Date(now)

    if (label === '7') start.setDate(now.getDate() - 6)
    else if (label === '15') start.setDate(now.getDate() - 14)
    else if (label === '30') start.setDate(now.getDate() - 29)

    return {
      label:
        label === '7'
          ? 'Last 7 days'
          : label === '15'
            ? 'Last 15 days'
            : 'Last 30 days',
      startDate: formatDate(start),
      endDate: formatDate(end),
    }
  }

  const handleFilterApply = (selectedFilters: Record<string, any>) => {
    const selected = selectedFilters?.date_filter

    if (['7', '15', '30'].includes(selected)) {
      setDateFilter(computeRange(selected))
    } else if (
      selected === 'custom' &&
      selectedFilters.fromDate &&
      selectedFilters.toDate
    ) {
      setDateFilter({
        label: 'Custom Range',
        startDate: selectedFilters.fromDate,
        endDate: selectedFilters.toDate,
      })
    } else {
      setDateFilter(null)
      resetData()
    }
  }

  const handleClearFilter = () => {
    setDateFilter(null)
    resetData()
  }

  useEffect(()=>{
      trackEvent(CustomEventTypes.CropDocImagePastDiagnosisListingUserLanded)
  },[])

  return (
    <LinearGradient
      colors={['#00503B', '#108733', '#00503B']}
      locations={[1, 0.75, 1]}
      start={{ x: 0.5, y: 0 }}
      end={{ x: 0.5, y: 1 }}
      style={[COMMON_STYLES.flex1]}
    >
      <SafeAreaView style={[COMMON_STYLES.flex1]}>
        <View
          style={[
            { padding: 20, gap: 8 },
            COMMON_STYLES.row,
            COMMON_STYLES.alignItemsCenter,
          ]}
        >
          <TouchableOpacity
            onPress={() => {
              navigation.goBack()
            }}
          >
            <Image
              source={BACK_ARROW_BUTTON}
              style={{ width: 24, height: 24 }}
            />
          </TouchableOpacity>
          <Text
            style={[
              TYPOGRAPHY.HEADING2,
              VARIANTS_STYLES.EMPHASIZED,
              TEXT_STYLES.textWhite,
            ]}
          >
            Your Previous Diagnosis
          </Text>
        </View>

        <View
          style={[
            COMMON_STYLES.flex1,
            COMMON_STYLES.ofHidden,
            {
              backgroundColor: '#F7F8FA',
              borderTopLeftRadius: 32,
              borderTopRightRadius: 32,
            },
          ]}
        >
          <FlatList
            data={isFilteredEmpty ? [] : diagnosisData}
            keyExtractor={item => item.plantix_id.toString()}
            ListHeaderComponent={
              <>
                <FilterButton
                  isFilterActive={!!dateFilter}
                  onFilterClick={() => setShowFilterModal(true)}
                  appliedFilterLabel={dateFilter?.label || ''}
                  onClearFilter={handleClearFilter}
                />
              </>
            }
            ListEmptyComponent={
              !loading && isFilteredEmpty ? (
                <Text style={{ paddingTop: 20, textAlign: 'center' }}>
                  No diagnosis found for this filter.
                </Text>
              ) : null
            }
            renderItem={({ item }) => (
              <DiagnosisCard
                key={item.plantix_id}
                date={new Date(item.created_date * 1000).toLocaleDateString(
                  'en-IN',
                  {
                    day: 'numeric',
                    month: 'short',
                  },
                )}
                id={item.plantix_id}
                title={item.disease_name}
                crop={item.crop_name_local}
                onPress={() => {
                  dispatch(setPlantixUId(item.plantix_id))
                  rnNavigation({
                    name: FARMER_360_ROUTES.DISEASE_TREATMENT,
                    params: {
                      diseaseId: item.disease_id,
                      cropName: item.crop_name_local!,
                      cropId:  item.crop_id
                    },
                  })
                  
                            trackEvent(CustomEventTypes.CropDocImagePastDiagnosisSelected,{
                              DiseaseID:item.disease_id,
                              DiseaseName:item.disease_name,
                              CropID:item.crop_id,
                              CropName:item.crop_name_local,
                              FarmerID:farmerId,
                              FarmerName:farmerName,
                            })
                }}
              />
            )}
            contentContainerStyle={[
              COMMON_STYLES.flexGrow,
              COMMON_STYLES.ph1,
              { paddingTop: 20 },
            ]}
            showsVerticalScrollIndicator={false}
            onEndReached={() => {
              if (hasMore) fetchNextPage()
            }}
            onEndReachedThreshold={0.7}
          />
        </View>

        {showFilterModal && (
          <FilterModal
            appliedFilterData={{
              date_filter:
                dateFilter?.label === 'Custom Range'
                  ? 'custom'
                  : dateFilter?.label?.match(/\d+/)?.[0] || '',
              fromDate: dateFilter?.startDate || '',
              toDate: dateFilter?.endDate || '',
            }}
            onClose={() => setShowFilterModal(false)}
            onApplyFilters={filters => {
              setShowFilterModal(false)
              handleFilterApply(filters)
            }}
            filterList={[
              {
                filterJsonKey: 'date_filter',
                filterName: 'Date',
                inputType: FORM_INPUT_TYPE.DROPDOWN,
                label: 'Select Date',
                filterData: [
                  { id: '7', value: 'Last 7 days' },
                  { id: '15', value: 'Last 15 days' },
                  { id: '30', value: 'Last 30 days' },
                  { id: 'custom', value: 'Custom Range' },
                ],
              },
            ]}
          />
        )}
      </SafeAreaView>
    </LinearGradient>
  )
}

export default PreviousDiagnosis
